<script setup>
import { onBeforeUnmount, onMounted, ref, shallowRef } from 'vue'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { Cloud, Clouds } from '@pmndrs/vanilla'
import { BlendFunction, BloomEffect, EdgeDetectionMode, EffectComposer, EffectPass, NoiseEffect, RenderPass, SMAAEffect, SMAAPreset, GodRaysEffect, KernelSize, BrightnessContrastEffect } from 'postprocessing'
import gsap from 'gsap'

import CustomGUI from '../../utils/customGUI'
import { VignetteEffect } from '../../utils/getVignetteEffect'

const props = defineProps({
	onLoad: {
		type: Function,
		default: null,
	},
})

const isLoaded = ref(false)

// ============================================
// CONFIGURATION
// ============================================
const CONFIG = {
	urls: {
		dracoLoader: 'https://papercrowns.s3.amazonaws.com/website/scene/draco/',
		castleModel: 'https://papercrowns.s3.amazonaws.com/website/scene/humble-castle-low.glb',
		environment: 'https://papercrowns.s3.amazonaws.com/website/scene/textures/environment.hdr',
		moonTexture: 'https://papercrowns.s3.amazonaws.com/website/scene/textures/moon_map.webp',
		cloudTexture: 'https://papercrowns.s3.amazonaws.com/website/scene/textures/cloud_map.webp',
	},
	performance: {
		lowEnd: {
			pixelRatio: 1,
			shadowMapSize: 256,
			fogDensity: 0.2,
			enablePostProcessing: false,
			enableThunder: false,
			enableGodRays: false,
		},
		highEnd: {
			pixelRatio: 1.5,
			shadowMapSize: 512,
			fogDensity: 0.3,
			enablePostProcessing: true,
			enableThunder: true,
			enableGodRays: true,
		},
	},
	scene: {
		fogColor: 0x1d252f,
		moonPosition: { x: 111, y: 50, z: 80 },
		moonScale: 18,
		cameraPosition: { x: 0, y: -1, z: 3.5 },
		lookAtHeight: -0.25,
	},
	controls: {
		rotationSpeed: 0.5,
		dampingFactor: 0.05,
		scrollInfluence: 0.001,
		minPolarAngle: Math.PI / 2,
		maxPolarAngle: Math.PI / 2,
	},
	followLight: {
		surfaceOffsetDistance: 0.8,
	},
	vignette: {
		custom: {
			offset: 1.25,
			darkness: 0,
			smoothness: 1.5,
			color: 0x080808,
			ellipticalAmount: 0.75,
		},
		animation: {
			enabled: true,
			speed: 1,
			intensity: 0.5,
		},
	},
}

// ============================================
// COMPOSABLES & UTILITIES
// ============================================
const isLowEndDevice = () => {
	const ua = navigator.userAgent
	const isMobile = /iPhone|Android|Mobile/i.test(ua)
	const hasLowMemory = navigator.deviceMemory ? navigator.deviceMemory <= 4 : false
	return isMobile || hasLowMemory
}

// Throttle function for events
const throttle = (func, limit) => {
	let inThrottle
	return function (...args) {
		if (!inThrottle) {
			func.apply(this, args)
			inThrottle = true
			setTimeout(() => (inThrottle = false), limit)
		}
	}
}

// Debounce function for resize
const debounce = (func, wait) => {
	let timeout
	return function (...args) {
		clearTimeout(timeout)
		timeout = setTimeout(() => func.apply(this, args), wait)
	}
}

// ============================================
// STATE MANAGEMENT
// ============================================
const sceneContainer = ref(null)

// Use shallowRef for Three.js objects (performance boost)
const state = {
	scene: shallowRef(null),
	camera: shallowRef(null),
	renderer: shallowRef(null),
	composer: shallowRef(null),
	moon: shallowRef(null),
	castleModel: shallowRef(null),
	cloudsGroup: shallowRef(null),
	lights: [],
	animations: {
		moonTimeline: null,
		frameId: null,
		mixer: null, // Animation mixer for GLTF animations
	},
	raycaster: new THREE.Raycaster(),
	mouse: new THREE.Vector2(),
	followLight: null,
	isFollowLightEnabled: false,
	played: false,
	cameraControls: {
		isDragging: false,
		previousMousePosition: { x: 0, y: 0 },
		rotation: { x: -0.25, y: 0 },
		targetRotation: { x: 0, y: 0 },
		scrollDelta: 0,
	},
	effects: {
		customVignette: null,
	},
	activeLightCount: 0,
	hitBox: null,
	// gui: new CustomGUI(),
	gui: undefined,
}

state.gui?.addFolder('Camera')
state.gui?.addElement(CONFIG.scene, 'Rotation', { name: 'lookAtHeight', min: -1, max: 0.6, step: 0.01 }, 'Camera')
state.gui?.addElement(CONFIG.scene.cameraPosition, 'Height', { name: 'y', min: -2, max: 2, step: 0.01 }, 'Camera')

// Performance settings
const isMobile = isLowEndDevice()
const perfSettings = CONFIG.performance[isMobile ? 'lowEnd' : 'highEnd']

// ============================================
// SCENE SETUP
// ============================================
const setupScene = () => {
	const sizes = {
		width: window.innerWidth,
		height: window.innerHeight,
		pixelRatio: Math.min(window.devicePixelRatio, perfSettings.pixelRatio),
	}
	if (isMobile && sizes.pixelRatio > 1) sizes.pixelRatio = 1

	// Scene
	state.scene.value = new THREE.Scene()
	state.scene.value.fog = new THREE.FogExp2(CONFIG.scene.fogColor, perfSettings.fogDensity)

	// Camera
	state.camera.value = new THREE.PerspectiveCamera(70, sizes.width / sizes.height, 0.1, 1000)
	state.camera.value.position.set(CONFIG.scene.cameraPosition.x, CONFIG.scene.cameraPosition.y, CONFIG.scene.cameraPosition.z)

	// Renderer with optimizations
	state.renderer.value = new THREE.WebGLRenderer({
		powerPreference: 'high-performance',
		antialias: false,
		stencil: false,
		depth: true,
	})
	state.renderer.value.setPixelRatio(sizes.pixelRatio)
	state.renderer.value.setSize(sizes.width, sizes.height)
	state.renderer.value.shadowMap.enabled = true
	state.renderer.value.shadowMap.type = THREE.PCFSoftShadowMap
	state.renderer.value.shadowMap.autoUpdate = false
	state.renderer.value.shadowMap.needsUpdate = true

	if (!isMobile) {
		state.renderer.value.physicallyCorrectLights = true
		state.renderer.value.toneMapping = THREE.ACESFilmicToneMapping
		state.renderer.value.toneMappingExposure = 1.0
	}

	// Context loss prevention
	const canvas = state.renderer.value.getContext().canvas
	canvas?.addEventListener('webglcontextlost', handleContextLost, false)
	canvas?.addEventListener('webglcontextrestored', handleContextRestored, false)

	sceneContainer.value.appendChild(state.renderer.value.domElement)

	return sizes
}

const handleContextLost = (e) => {
	e.preventDefault()
	console.warn('WebGL context lost')
	cancelAnimationFrame(state.animations.frameId)
}

const handleContextRestored = () => {
	console.log('WebGL context restored')
	animate()
}

// ============================================
// POST-PROCESSING
// ============================================
const setupPostProcessing = (camera) => {
	if (!perfSettings.enablePostProcessing) return

	state.composer.value = new EffectComposer(state.renderer.value)
	state.composer.value.addPass(new RenderPass(state.scene.value, camera))

	const bloom = new BloomEffect({
		luminanceThreshold: 0.05,
		luminanceSmoothing: 0.01,
		intensity: isMobile ? 0.8 : 1.2,
	})
	const noise = new NoiseEffect({
		blendFunction: BlendFunction.SCREEN,
		premultiply: true,
	})

	// const customVignette = new VignetteEffect({ ...CONFIG.vignette.custom })
	// state.effects.customVignette = customVignette

	const smaa = new SMAAEffect({
		preset: SMAAPreset.HIGH,
		edgeDetectionMode: EdgeDetectionMode.COLOR,
	})
	smaa.edgeDetectionMaterial.edgeDetectionThreshold = 0.1

	const brightnessContrast = new BrightnessContrastEffect({ brightness: 0.044, contrast: 0.208 })

	state.composer.value.addPass(
		new EffectPass(
			camera,
			bloom,
			noise,
			// customVignette,
			smaa,
			brightnessContrast,
		),
	)

	state.gui?.addFolder('Post processing effects')
	state.gui?.addElement(
		brightnessContrast,
		'Brightness',
		{
			name: 'brightness',
			min: -0.2,
			max: 0.2,
			step: 0.001,
		},
		'Post processing effects',
	)
	state.gui?.addElement(
		brightnessContrast,
		'Contrast',
		{
			name: 'contrast',
			min: -0.1,
			max: 0.3,
			step: 0.001,
		},
		'Post processing effects',
	)

	// return { customVignette }
}

// ============================================
// CUSTOM CAMERA CONTROLS
// ============================================
const setupCameraControls = () => {
	const controls = state.cameraControls

	// Mouse down event
	const onMouseDown = (event) => {
		controls.isDragging = true
		controls.previousMousePosition = {
			x: event.clientX,
			y: event.clientY,
		}

		sceneContainer.value.style.cursor = 'grabbing'
	}

	// Mouse move event
	const onMouseMove = (event) => {
		if (!controls.isDragging) return

		const deltaX = event.clientX - controls.previousMousePosition.x
		const deltaY = event.clientY - controls.previousMousePosition.y

		controls.targetRotation.y -= deltaX * CONFIG.controls.rotationSpeed * 0.01
		controls.targetRotation.x += deltaY * CONFIG.controls.rotationSpeed * 0.01

		// Clamp vertical rotation
		controls.targetRotation.x = Math.max(CONFIG.controls.minPolarAngle - Math.PI / 2, Math.min(CONFIG.controls.maxPolarAngle - Math.PI / 2, controls.targetRotation.x))

		controls.previousMousePosition = {
			x: event.clientX,
			y: event.clientY,
		}
	}

	// Mouse up event
	const onMouseUp = () => {
		controls.isDragging = false
		sceneContainer.value.style.cursor = 'grab'
	}

	// Wheel event for scroll-based rotation
	const onWheel = throttle((event) => {
		const delta = event.deltaY * CONFIG.controls.scrollInfluence
		controls.targetRotation.y += delta
	}, 16)

	state.cameraControlsListeners = { onMouseDown, onMouseMove, onMouseUp, onWheel }

	// Add event listeners
	if (sceneContainer.value) {
		sceneContainer.value.addEventListener('mousedown', onMouseDown)
		sceneContainer.value.addEventListener('mousemove', onMouseMove)
		sceneContainer.value.addEventListener('mouseup', onMouseUp)
	}

	window.addEventListener('wheel', onWheel, { passive: true })

	return { onMouseDown, onMouseMove, onMouseUp, onWheel }
}

const updateCamera = () => {
	const controls = state.cameraControls

	// Smooth damping
	controls.rotation.y += (controls.targetRotation.y - controls.rotation.y) * CONFIG.controls.dampingFactor

	// Apply rotation to camera
	const radius = CONFIG.scene.cameraPosition.z

	state.camera.value.position.x = radius * Math.sin(controls.rotation.y) * Math.cos(controls.rotation.x)
	state.camera.value.position.y = CONFIG.scene.cameraPosition.y
	state.camera.value.position.z = radius * Math.cos(controls.rotation.y) * Math.cos(controls.rotation.x)

	state.camera.value.lookAt(0, CONFIG.scene.lookAtHeight, 0)
}

// ============================================
// LIGHTS
// ============================================
const setupLights = () => {
	const thunder = new THREE.PointLight(0xffffff, 0, 100, 0.6)
	thunder.castShadow = !isMobile

	thunder.shadow.mapSize.set(perfSettings.shadowMapSize, perfSettings.shadowMapSize)
	thunder.shadow.bias = -0.005
	thunder.shadow.normalBias = 0.05
	thunder.shadow.radius = 2
	thunder.shadow.camera.near = 1
	thunder.shadow.camera.far = 50

	state.scene.value.add(thunder)

	if (perfSettings.enableThunder) {
		gsap.delayedCall(Math.random() * 5 + 2, () => animateThunder(thunder))
	}

	return { thunder }
}

const animateLight = (light, isMobile) => {
	// Determine next state
	const isCurrentlyOn = light.intensity > 0.1
	const targetIntensity = isCurrentlyOn ? 0 : light.userData.targetIntensity

	// Check if we can turn ON
	if (!isCurrentlyOn && state.activeLightCount >= 3 && targetIntensity > 0.1) {
		const retryDelay = Math.random() * 2000 + 1000
		setTimeout(() => animateLight(light, isMobile), retryDelay)
		return
	}

	const delay = Math.random() * (isMobile ? 10000 : 7500)

	// Update active light count
	if (targetIntensity > 0.1) {
		state.activeLightCount++
	} else if (isCurrentlyOn) {
		state.activeLightCount = Math.max(0, state.activeLightCount - 1)
	}

	gsap.to(light, {
		intensity: targetIntensity,
		duration: 0.6,
		ease: 'bounce.inOut',
		onStart: () => {
			if (targetIntensity > 0.1) {
				if (!isMobile) {
					light.castShadow = true
					if (state.renderer?.value) state.renderer.value.shadowMap.needsUpdate = true
				}
				light.visible = true
			}
		},
		onComplete: () => {
			if (targetIntensity < 0.1) {
				if (!isMobile) {
					light.castShadow = false
					if (state.renderer?.value) state.renderer.value.shadowMap.needsUpdate = true
				}
				light.visible = false
			}

			setTimeout(() => animateLight(light, isMobile), delay)
		},
	})
}

const animateThunder = (thunder) => {
	const angle = Math.random() * Math.PI * 2
	const radius = 35

	thunder.position.set(radius * Math.cos(angle), 10, radius * Math.sin(angle))

	if (!isMobile && state.renderer.value) {
		state.renderer.value.shadowMap.needsUpdate = true
	}

	gsap.timeline({ defaults: { ease: 'bounce.inOut' } })
		.fromTo(thunder, { intensity: 0 }, { intensity: 50, duration: gsap.utils.random(0.25, 0.75, 0.05) })
		.to(thunder, {
			intensity: 0,
			duration: gsap.utils.random(0.25, 0.5, 0.05),
			onComplete: () => {
				setTimeout(() => animateThunder(thunder), Math.random() * 5000)
			},
		})
}

// ============================================
// MODEL LOADING - Optimized
// ============================================
const setupLoaders = () => {
	const manager = new THREE.LoadingManager()

	manager.onLoad = () => {
		isLoaded.value = true
		if (props.onLoad && typeof props.onLoad === 'function') {
			props.onLoad()
		}
	}

	manager.onError = (url) => {
		console.error('Error loading resource:', url)
	}

	const dracoLoader = new DRACOLoader(manager).setDecoderPath(CONFIG.urls.dracoLoader).setDecoderConfig({ type: 'wasm' })
	const gltfLoader = new GLTFLoader(manager).setDRACOLoader(dracoLoader)
	const hdrLoader = new HDRLoader(manager).setDataType(THREE.FloatType)
	const textureLoader = new THREE.TextureLoader(manager)

	return { gltfLoader, textureLoader, hdrLoader, manager }
}

const optimizeMaterial = (material, envMap) => {
	material.envMap = envMap
	material.envMapIntensity = 0.3
	material.needsUpdate = true
}

const loadCastle = (gltfLoader, hdrLoader) => {
	const pmremGenerator = new THREE.PMREMGenerator(state.renderer.value)
	pmremGenerator.compileEquirectangularShader()

	gltfLoader.load(
		CONFIG.urls.castleModel,
		(gltf) => {
			if (!state.scene.value) return

			const model = gltf.scene
			model.position.set(0, -1.5, 0)
			model.rotation.set(0, -2.5, 0)

			state.scene.value.add(model)
			state.castleModel.value = model

			// Setup animation mixer if animations exist
			if (gltf.animations && gltf.animations.length > 0) {
				state.animations.mixer = new THREE.AnimationMixer(model)

				// Play all animations in loop
				gltf.animations.forEach((clip) => {
					const action = state.animations.mixer.clipAction(clip)
					action.play()
				})
			}

			hdrLoader.load(
				CONFIG.urls.environment,
				(hdr) => {
					if (!state.scene.value) return

					const envMap = pmremGenerator.fromEquirectangular(hdr).texture

					state.scene.value.environment = envMap
					state.scene.value.background = envMap
					state.scene.value.backgroundBlurriness = 0.1
					state.scene.value.backgroundIntensity = 0.074

					state.gui?.addFolder('Lights')
					state.gui?.addFolder('Environment')
					state.gui?.addElement(state.scene.value, 'Brightness', { name: 'backgroundIntensity', min: 0, max: 0.1, step: 0.001 }, 'Environment')

					// Optimization: single traversal
					let index = 0
					model.traverse((child) => {
						if (child.isMesh && child.material) {
							child.castShadow = child.receiveShadow = true
							child.material.side = THREE.FrontSide
							child.geometry.computeVertexNormals()

							if (child.name === 'Shingles_low') child.material.normalScale.set(2, 2)

							optimizeMaterial(child.material, envMap)
						} else if (child.isPointLight) {
							if (child.name.includes('point')) {
								child.intensity = 0
								child.color = new THREE.Color(0xffae42)
								child.castShadow = !isMobile

								child.shadow.mapSize.set(perfSettings.shadowMapSize, perfSettings.shadowMapSize)
								child.shadow.bias = -0.001
								child.shadow.camera.near = 0.1
								child.shadow.camera.far = 5
								child.shadow.normalBias = 0.02

								child.userData.targetIntensity = 1.5
								state.lights.push(child)
							} else if (child.name.includes('ambient')) {
								if (index === 0) {
									child.intensity = 82
								} else if (index === 1) {
									child.intensity = 73
								} else {
									child.intensity = 23
								}
								// child.intensity = Math.round(child.intensity * 0.001)
								child.castShadow = true

								child.shadow.mapSize.set(perfSettings.shadowMapSize, perfSettings.shadowMapSize)
								child.shadow.bias = -0.001
								child.shadow.camera.near = 0.1
								child.shadow.camera.far = 5
								child.shadow.normalBias = 0.02

								const name = `Point ${child.name.toString().replace('ambient_', '')}`

								state.gui?.addElement(child, name, { name: 'intensity', min: 0, max: 200, step: 1 }, 'Lights')
								state.gui?.addElement(
									child,
									`${name} color`,
									{
										name: 'color',
										color: `#${child.color.getHexString()}`,
										onChange(value) {
											child.color = new THREE.Color(value)
										},
									},
									'Lights',
								)
								index++
							}
						}
					})

					// Cleanup
					hdr.dispose()
					pmremGenerator.dispose()
					THREE.Cache.clear()

					state.lights.forEach((light, index) => {
						const delay = index * (isMobile ? 1500 : 1000)
						setTimeout(() => {
							animateLight(light, isMobile)
						}, delay)
					})
				},
				undefined,
				(error) => console.error('Error loading HDR environment:', error),
			)
		},
		undefined,
		(error) => console.error('Error loading main model:', error),
	)
}

// ============================================
// CLOUDS
// ============================================
const loadClouds = (textureLoader) => {
	textureLoader.load(
		CONFIG.urls.cloudTexture,
		(tex) => {
			if (!state.scene.value) return

			state.cloudsGroup.value = new Clouds({
				texture: tex,
				material: THREE.MeshBasicMaterial,
			})
			state.cloudsGroup.value.scale.set(0.5, 0.5, 0.5)
			state.cloudsGroup.value.position.set(0, -2, 0)

			state.scene.value.add(state.cloudsGroup.value)

			const cloudSettings = {
				speed: 0.1,
				growth: 5,
				smallestVolume: 3.5,
				volume: 5.5,
				color: new THREE.Color('#223344'),
			}

			const cloud_0 = new Cloud({
				bounds: new THREE.Vector3(1, 0.5, 1),
				...cloudSettings,
			})
			const cloud_1 = new Cloud({
				bounds: new THREE.Vector3(20, 1, 20),
				...cloudSettings,
			})

			cloud_0.position.set(0, -9, 0)
			cloud_1.position.set(0, -5, 0)

			state.cloudsGroup.value.add(cloud_0, cloud_1)
		},
		undefined,
		(error) => console.error('Error loading cloud texture:', error),
	)
}

// ============================================
// MOON & INTERACTIONS
// ============================================
const setupFollowLight = () => {
	state.followLight = new THREE.PointLight(0xffae42, 20, 10)
	state.followLight.castShadow = false
	state.followLight.shadow.mapSize.set(512, 512)
	state.followLight.position.set(1, 7, 1)
	state.followLight.visible = false

	const hitBoxGeo = new THREE.BoxGeometry(1.5, 4, 1.5)
	const hitBoxMat = new THREE.MeshBasicMaterial({
		color: 0xff0000,
		visible: false,
		wireframe: true,
	})
	const hitBox = new THREE.Mesh(hitBoxGeo, hitBoxMat)
	hitBox.position.set(0.15, 0, 0)
	hitBox.name = 'HitBox'

	state.scene.value.add(state.followLight, hitBox)

	state.hitBox = hitBox

	return { hitBox }
}

const loadMoon = (textureLoader, camera) => {
	textureLoader.load(CONFIG.urls.moonTexture, (tex) => {
		const moonGeo = new THREE.SphereGeometry(1, 13, 13)
		const moonMat = new THREE.MeshBasicMaterial({
			map: tex,
			fog: false,
			color: new THREE.Color('#fff8e7'),
		})

		state.moon.value = new THREE.Mesh(moonGeo, moonMat)
		state.moon.value.position.set(CONFIG.scene.moonPosition.x, CONFIG.scene.moonPosition.y, CONFIG.scene.moonPosition.z)
		state.moon.value.scale.setScalar(CONFIG.scene.moonScale)
		state.moon.value.name = 'Moon'
		state.moon.value.castShadow = state.moon.value.receiveShadow = false

		state.scene.value.add(state.moon.value)

		createMoonAnimation()

		if (perfSettings.enableGodRays && state.composer.value) {
			const godRays = new GodRaysEffect(camera, state.moon.value, {
				height: 256,
				kernelSize: KernelSize.SMALL,
				density: 0.73,
				decay: 0.92,
				weight: 0.16,
				exposure: 0.4,
				samples: isMobile ? 16 : 32,
				clampMax: 1.0,
			})

			state.composer.value.addPass(new EffectPass(camera, godRays))

			const folder = state.gui?.addFolder('Moon rays', 'Post processing effects')

			state.gui?.addElement(godRays, 'Resolution', { name: 'height', range: [8, 16, 32, 64, 128, 256, 512] }, folder)
			state.gui?.addElement(godRays, 'Blurriness', { name: 'kernelSize', range: KernelSize }, folder)
			state.gui?.addElement(godRays.godRaysMaterial.uniforms.density, 'Rays density', { name: 'value', min: 0.5, max: 1, step: 0.01 }, folder)
			state.gui?.addElement(godRays.godRaysMaterial.uniforms.weight, 'Rays weight', { name: 'value', min: 0.01, max: 0.5, step: 0.01 }, folder)
		}
	})
}

const createMoonAnimation = () => {
	const color = { value: '#fff8e7' }
	const backgroundRotation = state.scene.value.backgroundRotation
	const environmentIntensity = { value: 0.3 }

	state.animations.moonTimeline = gsap
		.timeline({
			paused: true,
			defaults: { duration: 3, ease: 'power1.inOut' },
		})
		.to(
			color,
			{
				value: '#404040',
				ease: 'bounce.inOut',
				onUpdate: () => {
					if (state.moon.value) {
						state.moon.value.material.color = new THREE.Color(color.value)
					}
				},
			},
			0,
		)
		.to(state.moon.value.position, { y: -2 }, 0)
		.to(backgroundRotation, { x: 0.5, y: 0.06 }, 0)
		.to(state.scene.value, { backgroundIntensity: 0.05 }, 0)
		.to(
			environmentIntensity,
			{
				value: 0.1,
				onUpdate: () => {
					state.castleModel.value?.traverse((child) => {
						if (child.isMesh && child.material) {
							child.material.envMapIntensity = environmentIntensity.value
						}
					})
				},
			},
			0,
		)
		.call(
			() => {
				if (state.followLight) state.followLight.visible = false
			},
			null,
			0,
		)
		.call(
			() => {
				if (state.followLight) state.followLight.visible = true
			},
			null,
			1.25,
		)
}

// ============================================
// EVENT HANDLERS - With throttling
// ============================================
const handleMouseMove = throttle((event) => {
	const sizes = {
		width: window.innerWidth,
		height: window.innerHeight,
	}

	state.mouse.x = (event.clientX / sizes.width) * 2 - 1
	state.mouse.y = -(event.clientY / sizes.height) * 2 + 1

	state.raycaster.setFromCamera(state.mouse, state.camera.value)

	// OPTIMIZE: Single raycaster call for multiple checks
	let moonIntersects = []
	let hitBoxIntersects = []

	if (state.moon.value) {
		moonIntersects = state.raycaster.intersectObject(state.moon.value, false)

		if (moonIntersects.length && sceneContainer.value) {
			sceneContainer.value.style.cursor = 'pointer'
		} else {
			sceneContainer.value.style.cursor = state.cameraControls.isDragging ? 'grabbing' : 'grab'
		}
	}

	// CHANGE: Only raycast if follow light is enabled
	if (state.isFollowLightEnabled && state.castleModel.value && state.followLight) {
		if (state.hitBox) {
			hitBoxIntersects = state.raycaster.intersectObject(state.hitBox, false)

			if (hitBoxIntersects.length) {
				const pt = hitBoxIntersects[0].point
				const normal = hitBoxIntersects[0].face.normal.clone().multiplyScalar(CONFIG.followLight.surfaceOffsetDistance)
				const targetPos = pt.clone().add(normal)

				gsap.to(state.followLight.position, {
					x: targetPos.x,
					y: targetPos.y,
					z: targetPos.z,
					duration: 0.2,
					ease: 'power2',
				})
			}
		}
	}
}, 16)

const handleClick = (event) => {
	if (!state.moon.value) return

	state.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
	state.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1

	state.raycaster.setFromCamera(state.mouse, state.camera.value)
	const intersects = state.raycaster.intersectObject(state.moon.value, false)

	if (intersects.length) {
		if (state.played) {
			state.played = false
			state.animations.moonTimeline?.reverse()
			state.isFollowLightEnabled = false
			gsap.to(state.followLight.position, {
				y: 7,
				ease: 'power4.in',
				duration: 1.25,
			})
		} else {
			state.played = true
			state.animations.moonTimeline?.play()
			gsap.to(state.followLight.position, {
				y: 1,
				delay: 2,
				ease: 'power4',
				duration: 1.25,
				onComplete: () => {
					state.isFollowLightEnabled = true
				},
			})
		}
	}
}

const handleResize = debounce(() => {
	const sizes = {
		width: window.innerWidth,
		height: window.innerHeight,
		pixelRatio: Math.min(window.devicePixelRatio, perfSettings.pixelRatio),
	}
	if (isMobile && sizes.pixelRatio > 1) sizes.pixelRatio = 1

	if (state.camera.value) {
		state.camera.value.aspect = sizes.width / sizes.height
		state.camera.value.updateProjectionMatrix()
	}

	if (state.renderer.value) {
		state.renderer.value.setSize(sizes.width, sizes.height)
		state.renderer.value.setPixelRatio(sizes.pixelRatio)
	}

	if (state.composer.value) {
		state.composer.value.setSize(sizes.width, sizes.height)
	}

	if (state.effects.customVignette) {
		state.effects.customVignette.setSize(sizes.width, sizes.height)
	}
}, 250)

// ============================================
// ANIMATION LOOP
// ============================================
const clock = new THREE.Clock()

function animate() {
	const delta = clock.getDelta()
	const elapsedTime = clock.getElapsedTime()

	// Update animation mixer for GLTF animations
	if (state.animations.mixer) {
		state.animations.mixer.update(delta)
	}

	// Moon rotation
	if (state.moon.value) {
		state.moon.value.rotation.y = elapsedTime * 0.1
	}

	// Update custom camera controls
	updateCamera()

	// Render
	if (state.composer.value && perfSettings.enablePostProcessing) {
		state.composer.value.render()
	} else if (state.renderer.value && state.scene.value && state.camera.value) {
		state.renderer.value.render(state.scene.value, state.camera.value)
	}

	// Update clouds
	if (state.cloudsGroup.value && state.camera.value) {
		state.cloudsGroup.value.update(state.camera.value, elapsedTime, delta)
	}

	// Update vignette
	if (CONFIG.vignette.animation.enabled && state.effects.customVignette) {
		const { speed, intensity } = CONFIG.vignette.animation
		const pulse = Math.sin(elapsedTime * speed) * intensity
		const baseDarkness = CONFIG.vignette.custom.darkness

		state.effects.customVignette.darkness = baseDarkness + pulse
	}

	state.animations.frameId = requestAnimationFrame(animate)
}

// ============================================
// CLEANUP - Improved
// ============================================
const cleanup = () => {
	// Cancel animation
	if (state.animations.frameId) {
		cancelAnimationFrame(state.animations.frameId)
		state.animations.frameId = null
	}

	// Remove event listeners
	window.removeEventListener('mousemove', handleMouseMove)
	window.removeEventListener('resize', handleResize)
	window.removeEventListener('click', handleClick)

	// Remove camera control listeners (stored references needed)
	const canvas = state.renderer.value?.domElement

	if (canvas && state.cameraControls) {
		const { onMouseDown, onMouseMove, onMouseUp, onWheel } = state.cameraControlsListeners || {}

		if (onMouseDown) canvas.removeEventListener('mousedown', onMouseDown)
		if (onMouseMove) canvas.removeEventListener('mousemove', onMouseMove)
		if (onMouseUp) canvas.removeEventListener('mouseup', onMouseUp)
		if (onWheel) window.removeEventListener('wheel', onWheel)
	}

	// Kill GSAP animations
	if (state.animations.moonTimeline) {
		state.animations.moonTimeline.kill()
		state.animations.moonTimeline = null
	}

	const tweenTargets = []
	if (state.followLight?.position) tweenTargets.push(state.followLight.position)
	if (state.moon?.value?.material?.color) tweenTargets.push(state.moon.value.material.color)
	if (state.castleModel?.value) tweenTargets.push(state.castleModel.value)

	if (tweenTargets.length > 0) {
		gsap.killTweensOf(tweenTargets)
	}

	// Stop animation mixer
	if (state.animations.mixer) {
		state.animations.mixer.stopAllAction()
		state.animations.mixer.uncacheRoot(state.castleModel.value)
		state.animations.mixer = null
	}

	// Dispose Three.js objects
	if (state.scene.value) {
		state.scene.value.traverse((obj) => {
			if (obj.isMesh) {
				obj.geometry?.dispose()
				if (Array.isArray(obj.material)) {
					obj.material.forEach((m) => m.dispose?.())
				} else {
					obj.material?.dispose?.()
				}
			}
			// Rimuovi texture
			if (obj.material?.map) {
				obj.material.map.dispose?.()
			}
		})
	}

	// After scene traversal disposal, ADD:
	if (state.cloudsGroup.value) {
		state.cloudsGroup.value.traverse((child) => {
			if (child.material) {
				if (child.material.map) child.material.map.dispose()
				child.material.dispose()
			}
			if (child.geometry) child.geometry.dispose()
		})
	}

	if (state.composer.value) {
		state.composer.value.passes.forEach((pass) => {
			if (pass.dispose) pass.dispose()
		})
		state.composer.value.dispose()
		state.composer.value = null
	}

	if (state.renderer.value) {
		state.renderer.value.dispose()
		const canvasEl = state.renderer.value.domElement
		canvasEl?.parentNode?.removeChild(canvasEl)
		state.renderer.value.forceContextLoss?.()
		state.renderer.value = null
	}

	if (state.scene.value) {
		while (state.scene.value.children.length > 0) {
			state.scene.value.remove(state.scene.value.children[0])
		}
		state.scene.value = null
	}

	// Clear state
	Object.keys(state).forEach((key) => {
		if (state[key]?.value !== undefined) {
			state[key].value = null
		}
	})

	state.gui?.destroy()
}

// ============================================
// LIFECYCLE
// ============================================
onMounted(() => {
	setupScene()
	setupPostProcessing(state.camera.value)
	setupCameraControls()
	setupLights()

	setupFollowLight()
	const { gltfLoader, textureLoader, hdrLoader } = setupLoaders()

	loadCastle(gltfLoader, hdrLoader)
	loadClouds(textureLoader)
	loadMoon(textureLoader, state.camera.value)

	// Event listeners
	window.addEventListener('mousemove', handleMouseMove)
	window.addEventListener('resize', handleResize)
	window.addEventListener('click', handleClick)

	// Start animation loop
	animate()
})

onBeforeUnmount(() => {
	cleanup()
})
</script>

<template>
	<main>
		<section class="scene">
			<div class="scene__fallback-veil" :class="{ 'scene__fallback-veil--loaded': isLoaded }" />
			<!-- <img class="scene__fallback" :class="{ 'scene__fallback--loaded': isLoaded }" src="https://papercrowns.s3.amazonaws.com/website/images/backgrounds/hero-castle.jpg" alt="Loading scene..." /> -->

			<div ref="sceneContainer" class="scene__container"></div>
		</section>
	</main>
</template>

<style lang="css">
.scene {
	@apply h-screen w-full overflow-hidden;
}

.scene__container {
	@apply h-full w-full;
}

.scene__fallback-veil {
	@apply absolute inset-0 z-20 h-full w-full transition-opacity duration-500 ease-in-out;

	background: #000000;
	background: linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 1) 100%);
}

.scene__fallback {
	@apply absolute inset-0 z-10 h-full w-full object-cover transition-opacity duration-500 ease-in-out;
}

.scene__fallback--loaded,
.scene__fallback-veil--loaded {
	@apply pointer-events-none opacity-0;
}

* {
	user-select: none;
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
}
</style>
